// Community browse / search / copy / start (PLAN.md Task 5.1; public browse Task 4.3).
// Anonymous users browse freely; copy, start, and AI CTAs become signup links.
// Detail is a full page (D-43), not a modal.

import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { CommunityCategory } from "@checklist/shared";
import { ErrorBanner } from "../components/AuthShell.js";
import { CreateWithAiModal } from "../components/CreateWithAiModal.js";
import { FeedbackThumbs } from "../components/feedback/FeedbackThumbs.js";
import { StartChecklistModal } from "../components/StartChecklistModal.js";
import { AnchorButton, Button, LinkButton } from "../components/ui/Button.js";
import { showToast } from "../components/ui/Toast.js";
import { useAuth } from "../context/AuthContext.js";
import {
  ApiError,
  copyCommunityList,
  fetchCommunityList,
  fetchCommunityLists,
  fetchCommunityTemplates,
  startCommunityList,
  type ApiCommunityListSummary,
  type ApiCommunityListDetail,
  type ApiCommunityTemplateSummary,
  type ApiList,
} from "../lib/api.js";
import {
  categoryFromSearch,
  categoryHref,
  categorySearch,
  shouldSyncQueryFromUrl,
  unknownCategoryMessage,
} from "../lib/communityCategory.js";
import {
  CommunityDetailView,
  CommunityIndexView,
  communityPairButtonClass,
  type CommunityCard,
  type CommunityDetail,
  type CommunityTemplateCard,
} from "./CommunityViews.js";
import { communityListTitle } from "../lib/seo.js";
import { useSignupMode } from "../hooks/useSignupMode.js";
import { signupLabel } from "../lib/signupCopy.js";

function countItems(list: ApiList): number {
  return list.items.length + list.subgroups.reduce((n, g) => n + g.items.length, 0);
}

function toCard(list: ApiCommunityListSummary): CommunityCard {
  return {
    id: list.id,
    name: list.name,
    description: list.description,
    itemCount: list.itemCount,
    ownerScreenName: list.ownerScreenName,
    official: list.official === true,
    copyCount: list.copyCount,
    aiGenerated: list.aiGenerated,
  };
}

function toTemplateCard(template: ApiCommunityTemplateSummary): CommunityTemplateCard {
  return {
    id: template.id,
    name: template.name,
    description: template.description,
    sectionNames: template.sectionNames,
    ownerScreenName: template.ownerScreenName,
    official: template.official === true,
    copyCount: template.copyCount,
  };
}

function toDetail(list: ApiCommunityListDetail): CommunityDetail {
  return {
    id: list.id,
    name: list.name,
    kind: list.kind,
    description: list.description,
    items: list.items.map((item) => ({ id: item.id, text: item.text })),
    subgroups: list.subgroups.map((group) => ({
      id: group.id,
      name: group.name,
      items: group.items.map((item) => ({ id: item.id, text: item.text })),
    })),
    ownerScreenName: list.ownerScreenName,
    official: list.official === true,
    copyCount: list.copyCount,
  };
}

export function CommunityPage() {
  const { mode: signupMode } = useSignupMode();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAnonymous = !user;
  const { id: detailId } = useParams<{ id?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") ?? "";
  const category = categoryFromSearch(searchParams.toString());
  const categoryNotice = unknownCategoryMessage(searchParams.toString());

  const [lists, setLists] = useState<ApiCommunityListSummary[]>([]);
  const [templates, setTemplates] = useState<ApiCommunityTemplateSummary[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(!detailId);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(initialQ);
  const [query, setQuery] = useState(initialQ);

  const [detail, setDetail] = useState<ApiCommunityListDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(Boolean(detailId));
  const [copyBusy, setCopyBusy] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  useEffect(() => {
    if (detailId && detail) document.title = communityListTitle(detail);
  }, [detail, detailId]);

  useEffect(() => {
    const q = searchParams.get("q") ?? "";
    if (!shouldSyncQueryFromUrl(q, query)) return;
    setSearchInput(q);
    setQuery(q);
  }, [searchParams, query]);

  function onDebouncedSearch(q: string) {
    setQuery(q);
    const params = new URLSearchParams(categorySearch(category, q));
    // categoryFromSearch drops an unknown slug; keep it so the notice stays (NT-030).
    if (!category && searchParams.has("category")) {
      params.set("category", searchParams.get("category") ?? "");
    }
    setSearchParams(params, { replace: true });
  }

  async function load(opts: {
    q: string;
    category: CommunityCategory | null;
    cursor?: string;
    append: boolean;
  }) {
    if (opts.append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      const listPromise = fetchCommunityLists({
        q: opts.q || undefined,
        cursor: opts.cursor,
        category: opts.category ?? undefined,
      });
      const templatePromise = opts.append
        ? Promise.resolve(null)
        : fetchCommunityTemplates({
            q: opts.q || undefined,
            category: opts.category ?? undefined,
          });
      const [res, templateRes] = await Promise.all([listPromise, templatePromise]);
      setLists((prev) => (opts.append ? [...prev, ...res.lists] : res.lists));
      setCursor(res.cursor);
      if (templateRes) setTemplates(templateRes.templates ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load community lists");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    if (detailId) return;
    void load({ q: query, category, append: false });
  }, [query, category, detailId]);

  useEffect(() => {
    if (!detailId) {
      setDetail(null);
      setDetailLoading(false);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setError(null);
    void (async () => {
      try {
        const list = await fetchCommunityList(detailId);
        if (!cancelled) setDetail(list);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Failed to load list");
        setDetail(null);
        navigate("/community", { replace: true });
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detailId, navigate]);

  async function onCopy() {
    if (!detail) return;
    setCopyBusy(true);
    setError(null);
    try {
      const copied = await copyCommunityList(detail.id);
      showToast(`Copied "${copied.name}" to My Lists`);
      navigate(`/lists/${copied.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Copy failed");
    } finally {
      setCopyBusy(false);
    }
  }

  const detailSignupHref = detail
    ? `/register?next=${encodeURIComponent(`/community/${detail.id}`)}`
    : "/register";

  const filtered = Boolean(query || category);
  let emptyBody: string;
  let emptyPrimary: ReactNode;
  if (filtered) {
    emptyBody = isAnonymous
      ? "Try a different search, or sign up and generate a list with AI."
      : "Try a different search, or generate a list with AI.";
  } else {
    emptyBody = isAnonymous
      ? signupLabel("publish", signupMode)
      : "Publish one of your lists to share it with everyone.";
  }
  if (filtered) {
    emptyPrimary = isAnonymous ? (
      <AnchorButton
        href={`/register?next=${encodeURIComponent(categoryHref(category, query))}`}
      >
        {signupLabel("aiCreate", signupMode)}
      </AnchorButton>
    ) : (
      <Button type="button" onClick={() => setAiOpen(true)}>
        Create with AI
      </Button>
    );
  } else {
    emptyPrimary = isAnonymous ? (
      <AnchorButton href="/register">{signupLabel("nav", signupMode)}</AnchorButton>
    ) : (
      <LinkButton to="/lists">Go to My Lists</LinkButton>
    );
  }

  if (detailId) {
    const cta = detail ? (
      isAnonymous ? (
        <AnchorButton href={detailSignupHref} fullWidth>
          {signupLabel("start", signupMode)}
        </AnchorButton>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            fullWidth
            className={communityPairButtonClass}
            onClick={() => setStartOpen(true)}
          >
            Start a checklist
          </Button>
          <Button
            type="button"
            variant="secondary"
            fullWidth
            className={communityPairButtonClass}
            disabled={copyBusy}
            onClick={() => void onCopy()}
          >
            {copyBusy ? "Copying…" : "Copy to my lists"}
          </Button>
        </div>
      )
    ) : undefined;

    const footer = detail ? (
      <FeedbackThumbs
        surfaceId="community.list-detail"
        payload={{
          itemCount: countItems(detail),
          copyCount: detail.copyCount,
          kind: detail.kind,
        }}
      />
    ) : undefined;

    return detailLoading || !detail ? (
      <article className="mx-auto min-h-[60vh] w-full max-w-2xl px-4 py-8 sm:py-10">
        <ErrorBanner message={error} />
        <p className="mb-6">
          <a
            href="/community"
            className="text-sm font-medium text-brand-700 no-underline hover:underline"
          >
            ← Community
          </a>
        </p>
        <p className="text-sm text-muted">Loading…</p>
      </article>
    ) : (
      <>
        <CommunityDetailView
          list={toDetail(detail)}
          cta={cta}
          footer={footer}
          banner={<ErrorBanner message={error} />}
        />
        {isAnonymous ? null : (
          <StartChecklistModal
            open={startOpen}
            sourceName={detail.name}
            onClose={() => setStartOpen(false)}
            onStart={(name) => startCommunityList(detail.id, name)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <CommunityIndexView
        lists={lists.map(toCard)}
        templates={templates.map(toTemplateCard)}
        category={category}
        query={query}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        onDebouncedSearch={onDebouncedSearch}
        categoryNotice={categoryNotice}
        loading={loading}
        loadingMore={loadingMore}
        cursor={cursor}
        onLoadMore={() => void load({ q: query, category, cursor, append: true })}
        emptyPrimary={emptyPrimary}
        emptyBody={emptyBody}
        banner={<ErrorBanner message={error} />}
      />
      {!isAnonymous ? (
        <CreateWithAiModal
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          initialPurpose={query}
        />
      ) : null}
    </>
  );
}
